import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, X, PackageCheck } from "lucide-react";

const n = (v) => Number(v || 0);

function VendorsTab() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const { data: suppliers = [] } = useQuery({ queryKey: ["mech-suppliers"], queryFn: async () => (await api.get("/suppliers")).data });

  const saveMut = useMutation({
    mutationFn: async () => editing ? api.put(`/suppliers/${editing.id}`, form) : api.post("/suppliers", form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mech-suppliers"] }); toast.success("Saved"); setOpen(false); },
    onError: () => toast.error("Save failed"),
  });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/suppliers/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["mech-suppliers"] }); toast.success("Deleted"); } });

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => { setEditing(null); setForm({ name: "", contact_name: "", phone: "", email: "", supplies: "", notes: "" }); setOpen(true); }} data-testid="add-supplier-btn"><Plus className="h-4 w-4 mr-1" />Add supplier</Button>
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Contact</TableHead><TableHead>Phone</TableHead><TableHead>Email</TableHead><TableHead>Supplies</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {suppliers.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No suppliers yet.</TableCell></TableRow>
            ) : suppliers.map((s) => (
              <TableRow key={s.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => navigate(`/mechanic/suppliers/${s.id}`)} data-testid={`supplier-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-sm">{s.contact_name || "—"}</TableCell>
                <TableCell className="text-sm">{s.phone || "—"}</TableCell>
                <TableCell className="text-sm">{s.email || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">{s.supplies || "—"}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setEditing(s); setForm({ ...s }); setOpen(true); }} data-testid={`edit-supplier-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this supplier?")) delMut.mutate(s.id); }}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit supplier" : "Add supplier"}</DialogTitle><DialogDescription>Vendor contact details.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1"><Label className="label-mono">Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div className="space-y-1"><Label className="label-mono">Contact person</Label><Input value={form.contact_name || ""} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div className="space-y-1"><Label className="label-mono">Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2 space-y-1"><Label className="label-mono">Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2 space-y-1"><Label className="label-mono">What they supply</Label><Input value={form.supplies || ""} onChange={(e) => setForm({ ...form, supplies: e.target.value })} placeholder="Oil, filters, brake parts…" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="supplier-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function POsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", supplier_name: "", items: [], notes: "" });
  const { data: pos = [] } = useQuery({ queryKey: ["mech-pos"], queryFn: async () => (await api.get("/purchase-orders")).data });
  const { data: suppliers = [] } = useQuery({ queryKey: ["mech-suppliers"], queryFn: async () => (await api.get("/suppliers")).data });
  const { data: parts = [] } = useQuery({ queryKey: ["mech-parts-all"], queryFn: async () => (await api.get("/parts")).data });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["mech-pos"] }); qc.invalidateQueries({ queryKey: ["mech-parts"] }); qc.invalidateQueries({ queryKey: ["mech-parts-all"] }); qc.invalidateQueries({ queryKey: ["mech-alerts"] }); };

  const saveMut = useMutation({
    mutationFn: async () => api.post("/purchase-orders", { ...form, items: form.items.filter((i) => i.name).map((i) => ({ ...i, qty: n(i.qty), unit_cost: n(i.unit_cost) })) }),
    onSuccess: () => { invalidate(); toast.success("PO created"); setOpen(false); },
    onError: () => toast.error("Save failed"),
  });
  const receiveMut = useMutation({
    mutationFn: async (id) => api.post(`/purchase-orders/${id}/receive`),
    onSuccess: () => { invalidate(); toast.success("PO received — stock added to inventory"); },
    onError: () => toast.error("Receive failed"),
  });
  const delMut = useMutation({ mutationFn: async (id) => api.delete(`/purchase-orders/${id}`), onSuccess: () => { invalidate(); toast.success("Deleted"); } });

  const addItem = () => setForm({ ...form, items: [...form.items, { part_id: "", name: "", qty: 1, unit_cost: "" }] });
  const updItem = (i, patch) => { const a = [...form.items]; a[i] = { ...a[i], ...patch }; setForm({ ...form, items: a }); };
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const pickPart = (i, id) => {
    if (id === "__custom") { updItem(i, { part_id: "" }); return; }
    const p = parts.find((x) => x.id === id);
    if (p) updItem(i, { part_id: id, name: p.name, unit_cost: p.unit_cost || "" });
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => { setForm({ supplier_id: "", supplier_name: "", items: [{ part_id: "", name: "", qty: 1, unit_cost: "" }], notes: "" }); setOpen(true); }} data-testid="new-po-btn"><Plus className="h-4 w-4 mr-1" />New purchase order</Button>
      </div>
      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>PO #</TableHead><TableHead>Supplier</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead>Ordered</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {pos.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No purchase orders yet.</TableCell></TableRow>
            ) : pos.map((p) => (
              <TableRow key={p.id} data-testid={`po-row-${p.id}`}>
                <TableCell className="font-mono-data font-medium">{p.po_number}</TableCell>
                <TableCell className="text-sm">{p.supplier_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[260px] truncate">{(p.items || []).map((i) => `${i.qty}× ${i.name}`).join(", ")}</TableCell>
                <TableCell className="text-right font-mono-data">{money(p.total)}</TableCell>
                <TableCell><Badge className={`border-0 ${p.status === "Received" ? "bg-green-500/12 text-green-400" : "bg-amber-500/12 text-amber-400"}`}>{p.status}</Badge></TableCell>
                <TableCell className="text-sm">{fmtDate(p.ordered_at)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {p.status !== "Received" && (
                    <Button variant="ghost" size="sm" onClick={() => receiveMut.mutate(p.id)} data-testid={`receive-po-${p.id}`}>
                      <PackageCheck className="h-4 w-4 mr-1 text-green-400" />Receive
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => window.confirm("Delete this PO?") && delMut.mutate(p.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New purchase order</DialogTitle><DialogDescription>Items linked to inventory are auto-stocked when the PO is received.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="label-mono">Supplier</Label>
              <Select value={form.supplier_id || undefined} onValueChange={(v) => { const s = suppliers.find((x) => x.id === v); setForm({ ...form, supplier_id: v, supplier_name: s?.name || "" }); }}>
                <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Choose supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="label-mono">Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="po-add-item"><Plus className="h-3 w-3 mr-1" />Add item</Button>
            </div>
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <Select value={it.part_id || "__custom"} onValueChange={(v) => pickPart(i, v)}>
                  <SelectTrigger className="col-span-4" data-testid={`po-item-pick-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__custom">Custom item</SelectItem>
                    {parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Item name" value={it.name} onChange={(e) => updItem(i, { name: e.target.value })} className="col-span-4" disabled={!!it.part_id} data-testid={`po-item-name-${i}`} />
                <Input type="number" placeholder="Qty" value={it.qty} onChange={(e) => updItem(i, { qty: e.target.value })} className="col-span-1" data-testid={`po-item-qty-${i}`} />
                <Input type="number" placeholder="$ ea" value={it.unit_cost} onChange={(e) => updItem(i, { unit_cost: e.target.value })} className="col-span-2" data-testid={`po-item-cost-${i}`} />
                <Button type="button" variant="ghost" size="icon" className="col-span-1" onClick={() => rmItem(i)}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            <div className="space-y-1"><Label className="label-mono">Notes</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <p className="text-sm font-mono-data text-right">Total: <b>{money(form.items.reduce((s, i) => s + n(i.qty) * n(i.unit_cost), 0))}</b></p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.items.some((i) => i.name) || saveMut.isPending} data-testid="po-save-btn">Create PO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Suppliers() {
  return (
    <div className="p-8" data-testid="mech-suppliers-page">
      <h1 className="font-heading text-3xl mb-1">Suppliers &amp; Purchasing</h1>
      <p className="text-muted-foreground mb-6">Vendors and purchase orders — received POs stock your inventory automatically.</p>
      <Tabs defaultValue="pos">
        <TabsList className="mb-4">
          <TabsTrigger value="pos" data-testid="tab-pos">Purchase Orders</TabsTrigger>
          <TabsTrigger value="vendors" data-testid="tab-vendors">Vendors</TabsTrigger>
        </TabsList>
        <TabsContent value="pos"><POsTab /></TabsContent>
        <TabsContent value="vendors"><VendorsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
